import { supabase } from "@/integrations/supabase/client";
import { ensureArtistProfileExists } from "./artistProfileUtils";

/**
 * Updates stream counts for both tracks and artists
 */
export async function updateStreamCounts(
  trackId: string,
  artistId: string,
  queryClient: any,
) {
  try {
    console.log(
      `Updating stream counts for track ${trackId} and artist ${artistId}`,
    );

    // First make sure artist profile exists
    await ensureArtistProfileExists(artistId);

    // Use database functions to increment the streams in a more reliable way
    // This avoids race conditions and potential data inconsistency
    const { data: updateResult, error: incrementError } = await supabase.rpc(
      "increment_track_streams",
      {
        p_track_id: trackId,
      },
    );

    if (incrementError) {
      console.error(
        "Error incrementing track stream count using RPC:",
        incrementError,
      );

      // Fall back to the original method if RPC fails
      const { data: trackData, error: fetchTrackError } = await supabase
        .from("tracks")
        .select("streams")
        .eq("id", trackId)
        .single();

      if (fetchTrackError) {
        console.error("Error fetching track stream count:", fetchTrackError);
        return false;
      }

      // Update track streams count
      const { error: trackError } = await supabase
        .from("tracks")
        .update({ streams: (trackData.streams || 0) + 1 })
        .eq("id", trackId);

      if (trackError) {
        console.error("Error updating track stream count:", trackError);
        return false;
      }
    }

    // Use database function to increment artist stream count
    const { data: artistUpdateResult, error: artistIncrementError } =
      await supabase.rpc("increment_artist_streams", {
        p_artist_id: artistId,
      });

    if (artistIncrementError) {
      console.error(
        "Error incrementing artist stream count using RPC:",
        artistIncrementError,
      );

      // Fall back to original method if RPC fails
      const { data: artistData, error: fetchArtistError } = await supabase
        .from("artist_profiles")
        .select("stream_count")
        .eq("id", artistId)
        .single();

      if (fetchArtistError) {
        console.error("Error fetching artist stream count:", fetchArtistError);
        return false;
      }

      // Update artist stream count
      const { error: artistError } = await supabase
        .from("artist_profiles")
        .update({ stream_count: (artistData.stream_count || 0) + 1 })
        .eq("id", artistId);

      if (artistError) {
        console.error("Error updating artist stream count:", artistError);
        return false;
      }
    }

    console.log("Successfully updated stream counts in database");

    // Invalidate related queries
    if (queryClient) {
      queryClient.invalidateQueries({ queryKey: ["tracks"] });
      queryClient.invalidateQueries({ queryKey: ["chartTracks"] });
      queryClient.invalidateQueries({ queryKey: ["artistTracks"] });
      queryClient.invalidateQueries({ queryKey: ["artistProfile"] });

      // Also invalidate any specific track query that might be cached
      queryClient.invalidateQueries({ queryKey: ["track", trackId] });
    }

    return true;
  } catch (error) {
    console.error("Error updating stream counts:", error);
    return false;
  }
}
